import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {PanelContentProps, PanelFooterProps, PanelHeaderProps} from '@Components/panel';
import {Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import {Text, TextSize} from '@Components/text';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import type {AiChatImageDownloadCompletedEventDetail} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';
import {
  AiChatImageDownloadEvent,
  checkIfAiChatImageHasUpscaledVersion,
  downloadGeneratedImageAsset,
} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';
import {AiChatCostType} from '@Libraries/ai-chat.library';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Pill, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './ai-chat-image-download-panel-content.module.scss';
import {ImageQualityOption} from './ai-chat-image-download-panel-content.types';
import {Size} from '@Components/button';
import {useIsInMobileModal} from '@Components/modal/hooks/useIsInMobileModal';


export interface AiChatImageDownloadPanelContentProps {
  id: string;
  className?: string;
  isOpenedInModal?: boolean;
  aiChatImageId: string;
  chatId: string;
}

export function AiChatImageDownloadPanelContent({className = '', isOpenedInModal = false, ...props}: AiChatImageDownloadPanelContentProps): ReactElement {
  const onClose = useDefaultPanelClose(props.id, isOpenedInModal);
  const [selectedFileType, setSelectedFileType] = useState<FileTypeOptions>(FileTypeOptions.PNG);
  const [selectedImageQuality, setSelectedImageQuality] = useState<ImageQualityOption>(ImageQualityOption.ORIGINAL);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isAlreadyUpscaled, setIsAlreadyUpscaled] = useState(false);
  const isMobileModal = useIsInMobileModal();

  useEffect(() => {
    void checkIfAiChatImageHasUpscaledVersion(props.aiChatImageId).then((upscaled) => {
      setIsAlreadyUpscaled(upscaled);
    });
  }, [props.aiChatImageId]);

  useEffect(() => {
    const handleDownloadCompleted = (event: Event): void => {
      const {hashedId, chatId, isEnhanced, success} = (event as CustomEvent<AiChatImageDownloadCompletedEventDetail>).detail;
      if (success && isEnhanced && hashedId === props.aiChatImageId && chatId === props.chatId) {
        setIsAlreadyUpscaled(true);
      }
    };

    window.addEventListener(AiChatImageDownloadEvent.COMPLETED, handleDownloadCompleted);

    return (): void => {
      window.removeEventListener(AiChatImageDownloadEvent.COMPLETED, handleDownloadCompleted);
    };
  }, [props.aiChatImageId, props.chatId]);

  const FILE_TYPE_OPTIONS = [
    {
      id: FileTypeOptions.PNG,
      icon: 'icon-image',
      text: window.i18next.t('pmwjs_png_image'),
    },
    {
      id: FileTypeOptions.PDF,
      icon: 'icon-doc',
      text: window.i18next.t('pmwjs_pdf_document'),
    },
  ];

  const IMAGE_QUALITY_OPTIONS = [
    {
      id: ImageQualityOption.ORIGINAL,
      text: window.i18next.t('pmwjs_original'),
    },
    {
      id: ImageQualityOption.ENHANCED_4K,
      text: window.i18next.t('pmwjs_enhanced_4k'),
    },
  ];

  const handleDownload = async (): Promise<void> => {
    setIsDownloading(true);
    onClose();
    try {
      await downloadGeneratedImageAsset(props.chatId, props.aiChatImageId, selectedImageQuality, selectedFileType);
    } catch (e: unknown) {
      console.error(e);
    } finally {
      setIsDownloading(false);
    }
  };

  const selectedFileTypeData = FILE_TYPE_OPTIONS.find((o) => o.id === selectedFileType) ?? FILE_TYPE_OPTIONS[0];
  const selectedImageQualityData = IMAGE_QUALITY_OPTIONS.find((o) => o.id === selectedImageQuality) ?? IMAGE_QUALITY_OPTIONS[0];

  const getFileTypeDropdownPopup = (): ReactElement => {
    return (
      <ControlledList
        className={styles.fileTypeOptionsList}
        onClick={(id): void => {
          setSelectedFileType(id as FileTypeOptions);
        }}
        items={FILE_TYPE_OPTIONS.map((option) => ({
          id: option.id,
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
          text: option.text,
          isSelected: selectedFileType === option.id,
          icon: option.icon,
          iconClasses: `${styles.fileTypeOptionIcon} ${selectedFileType === option.id ? styles.selectedFileTypeIcon : ''}`,
          className: `${styles.fileTypeOption} ${selectedFileType === option.id ? 'bg-primary-10' : ''}`,
        }))}
      />
    );
  };

  const getCreditPill = (): ReactElement => {
    return (
      <Pill
        className={styles.creditPill}
        size={PILL_SIZE.XSMALL}
        content={{
          type: PILL_CONTENT_TYPE.HYBRID,
          icon: 'icon-credits',
          iconColor: 'content-ai',
          iconSize: IconSize.SIZE_ICON_12,
          text: String(AiChatCostType.UPSCALE_IMAGE_COST),
          textColor: 'content-ai',
          isBold: true,
          textSize: TextSize.XXSMALL,
          gap: 1,
        }}
      />
    );
  };

  const getImageQualityDropdownPopup = (): ReactElement => {
    return (
      <ControlledList
        className={styles.imageQualityOptionsList}
        onClick={(id): void => {
          setSelectedImageQuality(id as ImageQualityOption);
        }}
        items={IMAGE_QUALITY_OPTIONS.map((option) => ({
          id: option.id,
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
          text: option.text,
          isSelected: selectedImageQuality === option.id,
          className: `${styles.imageQualityOption} ${selectedImageQuality === option.id ? 'bg-primary-10' : ''}`,
          ...(option.id === ImageQualityOption.ENHANCED_4K && !isAlreadyUpscaled ? {customElement: getCreditPill()} : {}),
        }))}
      />
    );
  };

  const getPanelHeader = (): ReactElement<PanelHeaderProps> => {
    return <PanelHeader title={window.i18next.t('pmwjs_download')} onClose={onClose} isOpenedInModal={isOpenedInModal} />;
  };

  const getFileTypeDropdown = (): ReactElement => {
    return (
      <div className="_full-width flex-h-row justify-content-between flex-items-center">
        <Text bold size={TextSize.XSMALL} val={window.i18next.t('pmwjs_file_type')} className={styles.dropdownLabel} />
        <Dropdown
          popupSpacingClass="spacing-p-t-3 spacing-p-b-4 spacing-p-r-4 spacing-p-l-4"
          position={DROPDOWN_POSITION.FIXED}
          alignment={ALIGNMENT_TYPE.BOTTOM_END}
          selector={
            <DropdownGhostSelectBox
              icon={selectedFileTypeData.icon}
              size={DROPDOWN_SIZE.SMALL}
              text={selectedFileTypeData.text}
              className={styles.fileTypeSelector}
              iconClassName={styles.fileTypeSelectorIcon}
              customIconSize={IconSize.SIZE_ICON_16}
            />
          }
          popup={getFileTypeDropdownPopup()}
          popUpHasCustomHeight
          showModalForSmallScreen
          maxHeight={300}
          modalTitle={window.i18next.t('pmwjs_file_type')}
          popupClassName={styles.fileTypePopup}
          modalShowBackButton
          modalShowCloseButton={false}
        />
      </div>
    );
  };

  const getImageQualityDropdown = (): ReactElement => {
    return (
      <div className="_full-width flex-h-row justify-content-between flex-items-center">
        <Text bold size={TextSize.XSMALL} val={window.i18next.t('pmwjs_image_quality')} className={styles.dropdownLabel} />
        <Dropdown
          popupSpacingClass="spacing-p-t-3 spacing-p-b-4 spacing-p-r-4 spacing-p-l-4"
          position={DROPDOWN_POSITION.FIXED}
          alignment={ALIGNMENT_TYPE.BOTTOM_END}
          selector={<DropdownGhostSelectBox size={DROPDOWN_SIZE.SMALL} text={selectedImageQualityData.text} className={styles.imageQualitySelector} />}
          popup={getImageQualityDropdownPopup()}
          popUpHasCustomHeight
          showModalForSmallScreen
          maxHeight={300}
          modalTitle={window.i18next.t('pmwjs_image_quality')}
          popupClassName={styles.imageQualityPopup}
          modalShowBackButton
          modalShowCloseButton={false}
        />
      </div>
    );
  };

  const getPanelContent = (): ReactElement<PanelContentProps> => {
    return (
      <PanelContent>
        <div className= {`${isMobileModal ? 'spacing-p-4' : 'spacing-p-6'} flex flexbox flex-v-row gap-5`}>
          {getFileTypeDropdown()}
          {getImageQualityDropdown()}
        </div>
      </PanelContent>
    );
  };

  const getPanelFooter = (): ReactElement<PanelFooterProps> => {
    return (
      <PanelFooter
        className={isMobileModal ? 'spacing-p-r-1 spacing-p-l-1 spacing-p-b-1' : 'spacing-p-r-3 spacing-p-l-3 spacing-p-b-3'}
        fullWidthPrimaryActions
        primaryActions={[
          <PanelFooterPrimaryDefaultAction
            key="download-button"
            isLoading={isDownloading}
            className="radius-8"
            icon="icon-cloud-download"
            onClick={(): void => {
              void handleDownload();
            }}
            text={window.i18next.t('pmwjs_download')}
            size={Size.SMALL}
          />,
        ]}
      />
    );
  };

  return <Panel className={className} panelHeader={getPanelHeader()} panelContent={getPanelContent()} panelFooter={getPanelFooter()} />;
}
